import { useState, type FormEvent } from 'react'
import type { PilotMembersPage } from '../types'

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})

type PilotCohortProps = {
  page: PilotMembersPage
  busy: boolean
  canManage: boolean
  onEnroll: (email: string, note: string) => Promise<boolean>
  onRemove: (userId: string) => Promise<boolean>
  onSearch: (query: string) => Promise<void>
  onRefresh: () => Promise<void>
}

export function PilotCohort({
  page,
  busy,
  canManage,
  onEnroll,
  onRemove,
  onSearch,
  onRefresh,
}: PilotCohortProps) {
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [query, setQuery] = useState('')
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null)
  const configuredMembers =
    page.cohort_total + page.static_hashes_configured
  const atCapacity = configuredMembers >= page.max_members

  const submitEnrollment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const normalizedEmail = email.trim()
    if (!normalizedEmail) return
    if (await onEnroll(normalizedEmail, note.trim())) {
      setEmail('')
      setNote('')
    }
  }

  const submitSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await onSearch(query.trim())
  }

  return (
    <section className="pilot-panel" id="account-pilot">
      <header className="pilot-heading">
        <div>
          <span className="eyebrow">Curic Account</span>
          <h2>Pilot cohort</h2>
          <p>
            {page.access_mode === 'pilot'
              ? 'Dynamic members take effect immediately. Removing one revokes every active browser session.'
              : 'Account access is open to all identities. Dynamic membership changes still revoke active browser sessions.'}
          </p>
        </div>
        <div className="pilot-stats" aria-label="Pilot cohort status">
          <span className={`mode-badge ${page.access_mode}`}>
            {page.access_mode} mode
          </span>
          <strong>{page.total}</strong>
          <span>dynamic members</span>
          <strong>{page.static_hashes_configured}</strong>
          <span>static hashes</span>
          <strong>
            {configuredMembers}/{page.max_members}
          </strong>
          <span>configured / cap</span>
        </div>
      </header>

      <div className="pilot-controls">
        {canManage ? (
          <form className="pilot-enroll-form" onSubmit={submitEnrollment}>
            <label>
              Customer email
              <input
                autoComplete="off"
                disabled={busy || atCapacity}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="customer@example.com"
                type="email"
                value={email}
              />
            </label>
            <label>
              Internal note
              <input
                disabled={busy || atCapacity}
                maxLength={500}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why this customer is in the pilot"
                value={note}
              />
            </label>
            <button
              className="primary-button"
              disabled={busy || atCapacity || !email.trim()}
              type="submit"
            >
              Enroll
            </button>
            {atCapacity ? (
              <p className="pilot-cap-note" role="status">
                Pilot cap reached. A reviewed deployment must raise the limit
                before another enrollment.
              </p>
            ) : null}
          </form>
        ) : (
          <p className="pilot-readonly-note">
            Admin role is required to change the cohort.
          </p>
        )}
        <form className="pilot-search" onSubmit={submitSearch}>
          <label>
            Search members
            <input
              disabled={busy}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name or email"
              value={query}
            />
          </label>
          <button disabled={busy} type="submit">
            Search
          </button>
          <button
            disabled={busy}
            onClick={() => void onRefresh()}
            type="button"
          >
            Refresh
          </button>
        </form>
      </div>

      <div className={`pilot-table-wrap ${busy ? 'is-loading' : ''}`}>
        <table className="pilot-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Enrollment</th>
              <th>Sessions</th>
              <th>Note</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {page.items.map((member) => (
              <tr key={member.user_id}>
                <td>
                  <strong>{member.display_name || 'Unnamed customer'}</strong>
                  <span>{member.email}</span>
                </td>
                <td>
                  <strong>{dateFormatter.format(new Date(member.enrolled_at))}</strong>
                  <span>by {member.enrolled_by}</span>
                </td>
                <td>{member.active_sessions}</td>
                <td>{member.note || '—'}</td>
                <td>
                  {canManage ? (
                    confirmingUserId === member.user_id ? (
                      <div className="pilot-confirm">
                        <button
                          className="danger-button"
                          disabled={busy}
                          onClick={async () => {
                            if (await onRemove(member.user_id)) {
                              setConfirmingUserId(null)
                            }
                          }}
                          type="button"
                        >
                          Confirm
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => setConfirmingUserId(null)}
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="text-danger-button"
                        disabled={busy}
                        onClick={() => setConfirmingUserId(member.user_id)}
                        type="button"
                      >
                        Remove
                      </button>
                    )
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {page.items.length === 0 ? (
          <div className="pilot-empty">
            No dynamic pilot members match this view.
          </div>
        ) : null}
      </div>
    </section>
  )
}
